import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ReportFilters } from '@/components/reports/report-filters'
import { PageLoader } from '@/components/ui/page-loader'
import { useReportStore } from '@/store/report-store'
import { reportsService } from '@/services/reports-service'
import { WinLossReport } from '@/components/reports/win-loss-report'
import { PipelineReport } from '@/components/reports/pipeline-report'
import { RevenueTypeReport } from '@/components/reports/revenue-type-report'
import { AvgDealSizeReport } from '@/components/reports/avg-deal-size-report'
import { EstimatorComparisonReport } from '@/components/reports/estimator-comparison-report'
import { MarkupAnalysisReport } from '@/components/reports/markup-analysis-report'
import { RejectionRateReport } from '@/components/reports/rejection-rate-report'
import { CycleTimeReport } from '@/components/reports/cycle-time-report'
import { ApprovalQueueReport } from '@/components/reports/approval-queue-report'
import { ResponseTimeReport } from '@/components/reports/response-time-report'
import { RevenueCollectedReport } from '@/components/reports/revenue-collected-report'
import { OpportunityQueueReport } from '@/components/reports/opportunity-queue-report'

const REPORT_META: Record<string, { title: string; category: string }> = {
  'win-loss': { title: 'Win / Loss Report', category: 'Sales' },
  'pipeline': { title: 'Pipeline Report', category: 'Sales' },
  'revenue-by-type': { title: 'Revenue by Project Type', category: 'Sales' },
  'avg-deal-size': { title: 'Average Deal Size', category: 'Sales' },
  'estimator-comparison': { title: 'Estimator Comparison', category: 'Performance' },
  'markup-analysis': { title: 'Markup Analysis', category: 'Performance' },
  'rejection-rate': { title: 'Rejection Rate', category: 'Performance' },
  'cycle-time': { title: 'Estimate Cycle Time', category: 'Efficiency' },
  'approval-queue': { title: 'Approval Queue Health', category: 'Efficiency' },
  'response-time': { title: 'Proposal Response Time', category: 'Efficiency' },
  'revenue-collected': { title: 'Revenue Collected', category: 'Sales' },
  'opportunity-queue': { title: 'Opportunity Queue', category: 'Efficiency' },
}

const REPORT_COMPONENTS: Record<string, React.FC<{ data: any; filters: any }>> = {
  'win-loss': WinLossReport,
  'pipeline': PipelineReport,
  'revenue-by-type': RevenueTypeReport,
  'avg-deal-size': AvgDealSizeReport,
  'estimator-comparison': EstimatorComparisonReport,
  'markup-analysis': MarkupAnalysisReport,
  'rejection-rate': RejectionRateReport,
  'cycle-time': CycleTimeReport,
  'approval-queue': ApprovalQueueReport,
  'response-time': ResponseTimeReport,
  'revenue-collected': RevenueCollectedReport,
  'opportunity-queue': OpportunityQueueReport,
}

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

export function ReportDetailPage() {
  const { reportId } = useParams<{ reportId: string }>()
  const navigate = useNavigate()
  const filters = useReportStore((s) => s.filters)
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const meta = REPORT_META[reportId || '']
  const ReportComponent = REPORT_COMPONENTS[reportId || '']

  const fetchReport = useCallback(async () => {
    if (!reportId || !ReportComponent) return
    setLoading(true)
    setError('')
    try {
      const result = await reportsService.getReport(reportId, {
        dateStart: filters.dateStart || undefined,
        dateEnd: filters.dateEnd || undefined,
        projectTypes: filters.projectTypes.length > 0 ? filters.projectTypes : undefined,
        estimatorId: filters.estimatorId || undefined,
        clientId: filters.clientId || undefined,
        status: filters.status || undefined,
        isPublicWorks: filters.isPublicWorks || undefined,
      })
      setData(result)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load report')
    } finally {
      setLoading(false)
    }
  }, [reportId, filters, ReportComponent])

  useEffect(() => {
    fetchReport()
  }, [fetchReport])

  if (!meta || !ReportComponent) {
    return (
      <div className="rep-detail">
        <div className="rep-detail-empty">
          <h2>Report not found</h2>
          <button className="rep-apply-btn" onClick={() => navigate('/reports')}>Back to Reports</button>
        </div>
      </div>
    )
  }

  function handleExportCSV() {
    if (!data?.tableData) return
    const rows = data.tableData
    if (rows.length === 0) return
    const headers = Object.keys(rows[0])
    const csv = [
      headers.join(','),
      ...rows.map((r: any) => headers.map((h) => JSON.stringify(r[h] ?? '')).join(',')),
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${reportId}-report.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="rep-detail">
      {/* Header */}
      <div className="rep-detail-header">
        <h1 className="rep-detail-title">{meta.title}</h1>
        <div className="rep-detail-header-right">
          {reportId !== 'opportunity-queue' && (
            <button className="rep-export-btn" onClick={handleExportCSV} disabled={!data?.tableData}>
              <DownloadIcon /> Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Body: Filters + Content */}
      <div className="rep-detail-body">
        <ReportFilters reportId={reportId!} onApply={fetchReport} />

        <main className="rep-content">
          {loading && (
            <PageLoader />
          )}

          {error && !loading && (
            <div className="rep-error">
              <p>{error}</p>
              <button className="rep-apply-btn" onClick={fetchReport}>Retry</button>
            </div>
          )}

          {!loading && !error && data && <ReportComponent data={data} filters={filters} />}
        </main>
      </div>
    </div>
  )
}
